import type { FormGroup, UntypedFormControl } from '@angular/forms';
import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import type { CustomLabels, TimerWidgetData } from '@web/widgets/timer/type/timer-widget.type';
import type { ETime, ETimerAppearanceSeparator } from '@common/enums';
import { ETimerType } from '@components/sp-timer/src/types/timer.type';

import { ChangeDetectionStrategy, Component } from '@angular/core';

import { ESize, EHorizontalPosition } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { WebDateTimePickerModule } from '@web/shared/components/web-date-time-picker/web-date-time-picker.module';
import { WebColorPickerComponent } from '@web/shared/components/web-color-picker/web-color-picker.component';
import { TimerAppearanceComponent } from './timer-appearance/timer-appearance.component';
import { TimerLegendSettingComponent } from './timer-legend-setting/timer-legend-setting.component';
import { SpDatetimeSliderComponent } from '@components/sp-datetime-slider/src/sp-datetime-slider.component';
import { PaidAccessNotificationContainerComponent } from '@web/shared/components/web-settings/paid-access-notification/paid-access-notification-container.component';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings';

@Component({
    selector: 'web-timer-control',
    templateUrl: './timer-control.component.html',
    styleUrls: ['./timer-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonElementsModule,
        WebColorPickerComponent,
        WebDateTimePickerModule,
        SpDatetimeSliderComponent,
        TimerAppearanceComponent,
        TimerLegendSettingComponent,
        PaidAccessNotificationContainerComponent,
        WebWidgetPositionSettingsComponent,
        WebWidgetHideSettingsComponent,
    ],
})
export class TimerControlComponent extends WebBaseControlComponent<TimerWidgetData> {
    public readonly ERadioGroupType = ERadioGroupType;
    public readonly ESize = ESize;
    public readonly EHorizontalPosition = EHorizontalPosition;
    public readonly ETimerType = ETimerType;

    public readonly MIN_DAYS = 0;
    public readonly MAX_DAYS = 100;

    public readonly MIN_HOURS = 0;
    public readonly MAX_HOURS = 23;

    public readonly MIN_MINUTES = 0;
    public readonly MAX_MINUTES = 59;

    public readonly MIN_SECONDS = 0;
    public readonly MAX_SECONDS = 59;

    public isEditLegendSettings = false;

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: this.fb.group({
                timerType: null,
                untilDate: null,
                remainingTime: this.fb.group({
                    days: null,
                    hours: null,
                    minutes: null,
                    seconds: null,
                }),
                autorestart: null,
                expiredText: null,
                columns: null,
                themeTextColor: null,
                themeDividerColor: null,
                size: null,
                customLabels: null,
                appearance: null,
                horizontalAlign: null,
                textColor: null,
                dividerColor: null,
                mobileSize: null,
                mobileHorizontalAlign: null,
                tabletSize: null,
                tabletHorizontalAlign: null,
                isSynchronizeStyle: null,
                hideOnMobile: null,
                hideOnTablet: null,
                hideOnDesktop: null,
                position: this.fb.group({
                    x: null,
                    tabletX: null,
                    mobileX: null,
                    y: null,
                    tabletY: null,
                    mobileY: null,
                    rotate: null,
                    tabletRotate: null,
                    mobileRotate: null,
                    zIndex: null,
                    tabletZIndex: null,
                    mobileZIndex: null,
                    width: null,
                    tabletWidth: null,
                    mobileWidth: null,
                }),
            }),
            styles: this.fb.group({}),
        });
    }

    protected setFormData(): void {
        this.controlForm.patchValue(
            {
                data: { ...this.widgetData },
                styles: { ...this.widgetStyles },
            },
            { emitEvent: false }
        );

        if (!this.widgetData.mobileHorizontalAlign) {
            this.data.patchValue({ mobileHorizontalAlign: this.widgetData.horizontalAlign }, { emitEvent: false });
        }

        if (!this.widgetData.mobileSize) {
            this.data.patchValue({ mobileSize: this.widgetData.size }, { emitEvent: false });
        }

        // What the view draws on tablet — mobile, or desktop on a timer with no mobile settings at all —
        // so the tablet tab shows it and the next edit saves it rather than the desktop value.
        const hasMobileSettings = Boolean(this.widgetData.mobileHorizontalAlign);

        if (!this.widgetData.tabletHorizontalAlign) {
            this.data.patchValue(
                { tabletHorizontalAlign: this.widgetData.mobileHorizontalAlign || this.widgetData.horizontalAlign },
                { emitEvent: false }
            );
        }

        if (!this.widgetData.tabletSize) {
            this.data.patchValue(
                { tabletSize: (hasMobileSettings && this.widgetData.mobileSize) || this.widgetData.size },
                { emitEvent: false }
            );
        }

        if (this.isSynchronizeStyle.value === null) {
            this.controlForm.patchValue(
                {
                    data: {
                        isSynchronizeStyle: true,
                    },
                },
                { emitEvent: false }
            );
        }
    }

    public get timerType(): UntypedFormControl {
        return this.data.get('timerType') as UntypedFormControl;
    }

    public get expiredText(): UntypedFormControl {
        return this.data.get('expiredText') as UntypedFormControl;
    }

    public get remainingTime(): UntypedFormControl {
        return this.data.get('remainingTime') as UntypedFormControl;
    }

    public get autorestart(): UntypedFormControl {
        return this.data.get('autorestart') as UntypedFormControl;
    }

    public get remainingTimeDays(): UntypedFormControl {
        return this.remainingTime.get('days') as UntypedFormControl;
    }

    public get remainingTimeHours(): UntypedFormControl {
        return this.remainingTime.get('hours') as UntypedFormControl;
    }

    public get remainingTimeMinutes(): UntypedFormControl {
        return this.remainingTime.get('minutes') as UntypedFormControl;
    }

    public get remainingTimeSeconds(): UntypedFormControl {
        return this.remainingTime.get('seconds') as UntypedFormControl;
    }

    public get appearance(): UntypedFormControl {
        return this.data.get('appearance') as UntypedFormControl;
    }

    public get textColor(): UntypedFormControl {
        return this.data.get('textColor') as UntypedFormControl;
    }

    public get themeTextColor(): UntypedFormControl {
        return this.data.get('themeTextColor') as UntypedFormControl;
    }

    public get dividerColor(): UntypedFormControl {
        return this.data.get('dividerColor') as UntypedFormControl;
    }

    public get themeDividerColor(): UntypedFormControl {
        return this.data.get('themeDividerColor') as UntypedFormControl;
    }

    public get horizontalAlign(): UntypedFormControl {
        return this.data.get('horizontalAlign') as UntypedFormControl;
    }

    public get size(): UntypedFormControl {
        return this.data.get('size') as UntypedFormControl;
    }

    public get mobileHorizontalAlign(): UntypedFormControl {
        return this.data.get('mobileHorizontalAlign') as UntypedFormControl;
    }

    public get mobileSize(): UntypedFormControl {
        return this.data.get('mobileSize') as UntypedFormControl;
    }

    public get tabletHorizontalAlign(): UntypedFormControl {
        return this.data.get('tabletHorizontalAlign') as UntypedFormControl;
    }

    public get tabletSize(): UntypedFormControl {
        return this.data.get('tabletSize') as UntypedFormControl;
    }

    public get columns(): UntypedFormControl {
        return this.data.get('columns') as UntypedFormControl;
    }

    public get customLabels(): UntypedFormControl {
        return this.data.get('customLabels') as UntypedFormControl;
    }

    public get untilDate(): UntypedFormControl {
        return this.data.get('untilDate') as UntypedFormControl;
    }

    public setTimerType(value: ETimerType): void {
        this.timerType.setValue(value);
    }

    public get position(): FormGroup<any> {
        return this.data.get('position') as FormGroup<any>;
    }

    public get hideOnMobile(): UntypedFormControl {
        return this.data.get('hideOnMobile') as UntypedFormControl;
    }

    public get hideOnTablet(): UntypedFormControl {
        return this.data.get('hideOnTablet') as UntypedFormControl;
    }

    public get hideOnDesktop(): UntypedFormControl {
        return this.data.get('hideOnDesktop') as UntypedFormControl;
    }

    public setUntilDate(value: Date): void {
        this.untilDate.setValue(value);
    }

    public setRemainingTimeDays(value: number): void {
        this.remainingTimeDays.setValue(value);
    }

    public setRemainingTimeHours(value: number): void {
        this.remainingTimeHours.setValue(value);
    }

    public setRemainingTimeMinutes(value: number): void {
        this.remainingTimeMinutes.setValue(value);
    }

    public setRemainingTimeSeconds(value: number): void {
        this.remainingTimeSeconds.setValue(value);
    }

    public setCustomLabels(value: CustomLabels): void {
        this.customLabels.setValue(value);
    }

    public setColumns(columns: ETime[]): void {
        return this.columns.setValue(columns);
    }

    public setAppearance(value: ETimerAppearanceSeparator): void {
        this.appearance.setValue(value);
    }

    public setSize(value: string): void {
        if (this.isSynchronizeStyle.value) {
            this.mobileSize.setValue(value);
            this.tabletSize.setValue(value);
        }
        this.size.setValue(value);
    }

    public setHorizontalAlign(value: string): void {
        if (this.isSynchronizeStyle.value) {
            this.mobileHorizontalAlign.setValue(value);
            this.tabletHorizontalAlign.setValue(value);
        }
        this.horizontalAlign.setValue(value);
    }

    public setMobileSize(value: string): void {
        this.mobileSize.setValue(value);
    }

    public setMobileHorizontalAlign(value: string): void {
        this.mobileHorizontalAlign.setValue(value);
    }

    public setTabletSize(value: string): void {
        this.tabletSize.setValue(value);
    }

    public setTabletHorizontalAlign(value: string): void {
        this.tabletHorizontalAlign.setValue(value);
    }

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.themeTextColor.setValue(presetName, { emitEvent: false });
        this.textColor.setValue(value);
    }

    public setDividerColor({ value, presetName }: ColorPickerEventPayload): void {
        this.themeDividerColor.setValue(presetName, { emitEvent: false });
        this.dividerColor.setValue(value);
    }

    public editTimerLegendSettings(): void {
        this.isEditLegendSettings = true;
    }

    public closeLegendSettings() {
        this.isEditLegendSettings = false;
    }

    public saveLegendSettings(value: CustomLabels) {
        this.setCustomLabels(value);
    }

    public onDateChange(date: Date) {
        this.setUntilDate(date);
    }

    public setSynchronizeStyle() {
        this.mobileHorizontalAlign.setValue(this.horizontalAlign.value);
        this.mobileSize.setValue(this.size.value);
        this.tabletHorizontalAlign.setValue(this.horizontalAlign.value);
        this.tabletSize.setValue(this.size.value);

        this.isSynchronizeStyle.setValue(!this.isSynchronizeStyle.value);
    }

    public get isSynchronizeStyle(): UntypedFormControl {
        return this.data.get('isSynchronizeStyle') as UntypedFormControl;
    }
}
